import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, field } from "@/components/nds/windows/finance/parts";

/** The open sit-down session (check-in id); loans can only be changed while one is open. */
export const SessionCtx = createContext(null);
export const useSessionCheckin = () => useContext(SessionCtx);

/** Loan balance owed, or false when there's no open session / no Loans permission. */
export function useLoan(residentId, anywhere = false) {
  const checkinId = useSessionCheckin();
  const [balance, setBalance] = useState(false);
  const load = () => (checkinId || anywhere ? finApi.get(`/loans/${residentId}`).then((d) => setBalance(d.balance)).catch(() => setBalance(false)) : setBalance(false));
  useEffect(() => { load(); }, [residentId, checkinId, anywhere]); // eslint-disable-line react-hooks/exhaustive-deps
  return { checkinId, balance, reload: load };
}

export const postLoan = (residentId, checkinId, loan, note, paycheckId) => finApi.post(`/loans/${residentId}`, { kind: loan.kind, amount: Number(loan.amount), note, checkin_id: checkinId, paycheck_id: paycheckId || null });

/** Loan row inside the paycheck correction form: add to the loan or record a repayment; the form's reason is the note. */
export function LoanFields({ id, balance, loan, setLoan }) {
  const over = loan.kind === "repay" && Number(loan.amount) > balance;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`fin-loan-fields-${id}`}>
      <span className="nds-label-dim text-[9px]">Line of Credit</span>
      {[["add", "Add to line of credit"], ["repay", "Repayment"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => setLoan((x) => ({ ...x, kind: k }))} data-testid={`fin-loan-kind-${k}-${id}`}
          className={`px-2.5 py-1 border rounded-sm font-mono text-[10px] uppercase tracking-[0.12em] ${loan.kind === k ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/15 text-nex-muted"}`}>{l}</button>))}
      <input type="number" min="0" step="0.01" value={loan.amount} onChange={(e) => setLoan((x) => ({ ...x, amount: e.target.value }))} placeholder="Amount"
        data-testid={`fin-loan-amount-${id}`} className={`${field} w-28`} />
      <span className="font-mono text-[10px] text-nex-muted" data-testid={`fin-loan-balance-${id}`}>Balance {money(balance)} owed</span>
      {over && <span className="text-[11px] text-amber-200" data-testid={`fin-loan-over-${id}`}>More than the balance owed</span>}
    </div>
  );
}

/** For staff with the Loans permission but no ledger-correction rights: the same loan row on a verified paycheck, with a note. */
export function LoanOnly({ pc, onChanged }) {
  const lo = useLoan(pc.resident_id);
  const [open, setOpen] = useState(false);
  const [loan, setLoan] = useState({ kind: "add", amount: "" });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (lo.balance === false) return null;
  const amt = Number(loan.amount) || 0;
  const ok = amt > 0 && note.trim() && !(loan.kind === "repay" && amt > lo.balance);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await postLoan(pc.resident_id, lo.checkinId, loan, note.trim(), pc.id); toast.success(loan.kind === "add" ? "Added to line of credit" : "Line of credit repayment recorded"); setLoan({ kind: loan.kind, amount: "" }); setNote(""); setOpen(false); lo.reload(); onChanged?.(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} data-testid={`fin-loan-open-${pc.id}`} className={btnGold}>Line of Credit</button>;
  return (
    <form onSubmit={save} data-testid={`fin-loan-form-${pc.id}`} className="p-3 border border-nex-gold/30 rounded-sm space-y-2">
      <LoanFields id={pc.id} balance={lo.balance} loan={loan} setLoan={setLoan} />
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Line of credit note (required)" data-testid={`fin-loan-note-${pc.id}`} className={`${field} w-full`} />
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setOpen(false)} data-testid={`fin-loan-cancel-${pc.id}`} className="ml-auto font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
        <button type="submit" disabled={busy || !ok} data-testid={`fin-loan-save-${pc.id}`} className={btnGold}>Save line of credit</button>
      </div>
    </form>
  );
}

/** Money step: penalties / restitution charges waiting to be verified — add each to the loan or waive it, with a note. */
export function PendingCharges({ residentId, onChanged }) {
  const checkinId = useSessionCheckin();
  const [rows, setRows] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);
  const load = () => finApi.get(`/loans/${residentId}/pending`).then((d) => setRows(d.rows)).catch(() => setRows(false));
  useEffect(() => { load(); }, [residentId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows || !rows.length) return null;
  const act = async (t, action) => {
    setBusy(t.id);
    try { const r = await finApi.post(`/loans/${residentId}/pending/${t.id}`, { action, note: notes[t.id] || "", checkin_id: checkinId }); setRows(r.pending.rows); toast.success(action === "add" ? "Added to line of credit" : "Charge waived"); onChanged?.(); }
    catch (e) { toast.error(finErr(e)); } finally { setBusy(null); }
  };
  return (
    <section className="p-3 border border-amber-400/30 rounded-sm space-y-2" data-testid="ss-pending-charges">
      <div className="text-sm text-amber-100 font-medium">Penalties to verify · {rows.length}</div>
      <p className="text-[11px] text-nex-muted">Verified charges are added to the line of credit. Waive any that shouldn't stand.</p>
      <ul className="space-y-2">{rows.map((t) => (
        <li key={t.id} className="space-y-1" data-testid={`ss-charge-${t.id}`}>
          <div className="flex items-start gap-2 text-xs"><span className="flex-1 min-w-0 text-nex-silver line-clamp-2">{new Date(t.created_at).toLocaleDateString()} · {t.description || "Penalty"}</span>
            <span className="font-mono text-nex-gold">{money(t.amount)}</span></div>
          <div className="flex flex-wrap gap-2">
            <input value={notes[t.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [t.id]: e.target.value }))} placeholder="Note (required)" data-testid={`ss-charge-note-${t.id}`} className={`${field} flex-1 min-w-[160px] !py-1`} />
            <button type="button" disabled={!!busy || !(notes[t.id] || "").trim()} onClick={() => act(t, "add")} data-testid={`ss-charge-add-${t.id}`} className={btnGold}>Add to line of credit</button>
            <button type="button" disabled={!!busy || !(notes[t.id] || "").trim()} onClick={() => act(t, "waive")} data-testid={`ss-charge-waive-${t.id}`} className="font-mono text-[10px] uppercase text-nex-muted hover:text-nex-silver disabled:opacity-40">Waive</button>
          </div>
        </li>))}</ul>
    </section>
  );
}
